import { useState, useEffect } from "react";
import { Tooltip } from "bootstrap";

import { useSampleEdit, getApiErrorInfo } from "../../api";

import OtherResultModal from "./OtherResults/OtherResultModal";
import DeleteResultModal from "./OtherResults/DeleteResultModal";
import { OtherResultCard } from "../ui/Cards";

export default function OtherResults({ id, otherResults }) {
  const [modalShow, setModalShow] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedDeleteResult, setSelectedDeleteResult] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isNewResult, setIsNewResult] = useState(false);
  const [newResultType, setNewResultType] = useState(null);
  // A failed save or delete, shown inside the modal it came from (kept
  // open so the entered result isn't lost).
  const [saveError, setSaveError] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const { mutateAsync, isLoading: editLoading } = useSampleEdit(id);

  useEffect(() => {
    let instances = [];
    const timeoutId = setTimeout(() => {
      const triggerEls = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      instances = [...triggerEls].map((el) => Tooltip.getOrCreateInstance(el));
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      instances.forEach((t) => t.dispose());
    };
  }, [otherResults]);

  async function handleSaveResult(resultData) {
    let updatedResults = [];
    if (isNewResult) {
      updatedResults = [...otherResults, resultData];
    } else {
      updatedResults = otherResults.map((result) =>
        result.resultId === resultData.resultId ? resultData : result,
      );
    }

    const data = {
      id: id,
      data: { otherResults: updatedResults },
    };

    setSaveError("");
    try {
      await mutateAsync(data);
    } catch (err) {
      setSaveError(
        getApiErrorInfo(err, "Failed to save this result. Please try again."),
      );
      return false;
    }

    setModalShow(false);
    setSelectedResult(null);
    setIsNewResult(false);
    setNewResultType(null);
    return true;
  }

  async function handleDeleteResult() {
    const updatedResults = otherResults.filter(
      (result) => result.resultId !== selectedDeleteResult.resultId,
    );

    const data = {
      id: id,
      data: { otherResults: updatedResults },
    };

    setDeleteError("");
    try {
      await mutateAsync(data);
    } catch (err) {
      setDeleteError(
        getApiErrorInfo(err, "Failed to delete this result. Please try again."),
      );
      return;
    }

    setShowDeleteModal(false);
    setSelectedDeleteResult(null);
  }

  return (
    <div className='my-2 full-height'>
      <div className='card full-height'>
        <div className='card-body d-flex flex-column'>
          <div
            className={
              otherResults && otherResults.length > 0
                ? "d-flex justify-content-between flex-wrap mb-2"
                : "full-height justify-content-between flex-grow-1"
            }
          >
            <h5 className='card-title text-primary'>
              Other Analytical Results
            </h5>
            <div
              className={`d-flex flex-row gap-2 ${
                otherResults && otherResults.length > 0
                  ? "justify-content-end"
                  : "full-height justify-content-center align-items-center"
              }`}
              tabIndex='0'
              {...(otherResults && otherResults.length >= 6
                ? {
                    "data-bs-toggle": "tooltip",
                    "data-bs-placement": "left",
                    "data-bs-title":
                      "Limit: 6 results. Please delete a result to add a new one.",
                  }
                : {})}
            >
              <button
                type='button'
                className='btn btn-outline-primary align-self-center'
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedResult(null);
                  setNewResultType("testStrip");
                  setModalShow(true);
                  setIsNewResult(true);
                }}
                disabled={otherResults && otherResults.length >= 6}
              >
                Add Test Strip Result
              </button>
              <button
                type='button'
                className='btn btn-outline-primary align-self-center'
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedResult(null);
                  setNewResultType("FTIR");
                  setModalShow(true);
                  setIsNewResult(true);
                }}
                disabled={otherResults && otherResults.length >= 6}
              >
                Add FTIR Result
              </button>
              <button
                type='button'
                className='btn btn-outline-primary align-self-center'
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedResult(null);
                  setNewResultType("otherTechnique");
                  setModalShow(true);
                  setIsNewResult(true);
                }}
                disabled={otherResults && otherResults.length >= 6}
              >
                Add Laboratory Result
              </button>
            </div>
          </div>
          {otherResults && otherResults.length > 0 && (
            <div className='d-flex flex-wrap justify-content-evenly gap-2'>
              {otherResults.map((result, index) => (
                <OtherResultCard
                  key={index}
                  result={result}
                  setSelectedResult={setSelectedResult}
                  setModalShow={setModalShow}
                  setShowDeleteModal={setShowDeleteModal}
                  setSelectedDeleteResult={setSelectedDeleteResult}
                  setIsNewResult={setIsNewResult}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <OtherResultModal
        show={modalShow}
        isNewResult={isNewResult}
        newResultType={newResultType}
        handleClose={() => {
          setModalShow(false);
          setSelectedResult(null);
          setIsNewResult(false);
          setNewResultType(null);
          setSaveError("");
        }}
        handleSave={(resultData) => handleSaveResult(resultData)}
        resultData={selectedResult}
        error={saveError}
        clearError={() => setSaveError("")}
        isSaving={editLoading}
      />
      <DeleteResultModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setSelectedDeleteResult(null);
          setDeleteError("");
        }}
        handleDelete={() => handleDeleteResult()}
        error={deleteError}
        clearError={() => setDeleteError("")}
        isDeleting={editLoading}
      />
    </div>
  );
}
